import { useState } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * SuggestionForm — inline form for mentors to leave suggestions on KB entries.
 *
 * Props:
 *   - targetRef: the selected entry's `sk` value (auto-populated when entry is selected)
 *   - onSuccess: callback invoked after successful submission
 *
 * Behaviour:
 *   - target_type is always 'kb_entry' when used in KB view
 *   - Validates comment is 1–2000 chars before enabling submit
 *   - Disables submit button during in-flight request (R7.6)
 *   - On success: shows confirmation, resets form (R7.3)
 *   - On 403: link inactive message (R7.4)
 *   - On 400: invalid data message (R7.4)
 *   - On 5xx/network: temporary failure + retry (R7.4)
 *
 * Requirements: 4.3, 4.7, 7.1–7.6
 */

export interface SuggestionFormProps {
  targetRef: string | null;
  onSuccess?: () => void;
}

type FormStatus =
  | { type: 'idle' }
  | { type: 'submitting' }
  | { type: 'success'; message: string }
  | { type: 'error'; errorKind: 'forbidden' | 'invalid' | 'temporary'; message: string };

const MAX_COMMENT_LENGTH = 2000;
const MIN_COMMENT_LENGTH = 1;

export default function SuggestionForm({ targetRef, onSuccess }: SuggestionFormProps) {
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<FormStatus>({ type: 'idle' });

  const trimmedComment = comment.trim();
  const commentLength = trimmedComment.length;
  const isCommentValid = commentLength >= MIN_COMMENT_LENGTH && commentLength <= MAX_COMMENT_LENGTH;
  const canSubmit = targetRef !== null && isCommentValid && status.type !== 'submitting';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !targetRef) return;

    setStatus({ type: 'submitting' });

    try {
      await api.post('/api/mentors/suggestions', {
        target_type: 'kb_entry',
        target_ref: targetRef,
        comment: trimmedComment,
      });
      setStatus({ type: 'success', message: 'Suggestion submitted successfully.' });
      setComment('');
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setStatus({
            type: 'error',
            errorKind: 'forbidden',
            message: 'Your mentor link is no longer active.',
          });
        } else if (err.status === 400) {
          setStatus({
            type: 'error',
            errorKind: 'invalid',
            message: 'The submitted data was invalid. Please check your input and try again.',
          });
        } else {
          setStatus({
            type: 'error',
            errorKind: 'temporary',
            message: 'A temporary failure occurred. Please try again.',
          });
        }
      } else {
        setStatus({
          type: 'error',
          errorKind: 'temporary',
          message: 'A temporary failure occurred. Please try again.',
        });
      }
    }
  }

  function handleRetry() {
    setStatus({ type: 'idle' });
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 border-t border-gray-200 pt-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Leave a Suggestion</h3>

      {/* Target reference display */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">Target Entry</label>
        {targetRef ? (
          <p className="text-xs text-gray-700 bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
            {targetRef}
          </p>
        ) : (
          <p className="text-xs text-gray-400 italic">Select a KB entry to leave a suggestion.</p>
        )}
      </div>

      {/* Comment textarea */}
      <div className="mb-3">
        <label htmlFor="suggestion-comment" className="block text-xs font-medium text-gray-500 mb-1">
          Comment
        </label>
        <textarea
          id="suggestion-comment"
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            // Clear error/success states when user starts typing
            if (status.type === 'success' || status.type === 'error') {
              setStatus({ type: 'idle' });
            }
          }}
          placeholder="Enter your suggestion or feedback…"
          rows={4}
          maxLength={MAX_COMMENT_LENGTH + 100} // Allow a bit over to show validation
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
          disabled={status.type === 'submitting'}
          aria-describedby="comment-char-count"
        />
        {/* Character count */}
        <p
          id="comment-char-count"
          className={`text-xs mt-1 ${
            commentLength > MAX_COMMENT_LENGTH ? 'text-red-600' : 'text-gray-500'
          }`}
        >
          {commentLength} / {MAX_COMMENT_LENGTH} characters
          {commentLength > MAX_COMMENT_LENGTH && ' (exceeds maximum)'}
        </p>
      </div>

      {/* Validation message for empty comment */}
      {targetRef && comment.length > 0 && !isCommentValid && commentLength === 0 && (
        <p className="text-xs text-amber-600 mb-2">Comment must be at least 1 character (after trimming whitespace).</p>
      )}

      {/* Status messages */}
      {status.type === 'success' && (
        <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3" role="status">
          <p className="text-sm text-green-700">{status.message}</p>
        </div>
      )}

      {status.type === 'error' && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
          <p className="text-sm text-red-700">{status.message}</p>
          {status.errorKind === 'temporary' && (
            <button
              type="button"
              onClick={handleRetry}
              className="mt-2 text-xs font-medium text-red-700 hover:text-red-900 underline"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={!canSubmit}
        className={`w-full text-sm font-medium py-2 px-4 rounded-lg transition-colors ${
          canSubmit
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
        aria-busy={status.type === 'submitting'}
      >
        {status.type === 'submitting' ? 'Submitting…' : 'Submit Suggestion'}
      </button>
    </form>
  );
}

// Export constants for testing
export { MAX_COMMENT_LENGTH, MIN_COMMENT_LENGTH };
